import React, { useMemo } from "react";

import useGetAdminConfig from "@/hooks/api/useGetAdminConfig";
import { SupText } from "@repo/core-ui";
import UiTooltip from "@/components/Ui/Tooltip";

export type SupRecordUnsupportedReason =
  | "vault"
  | "token"
  | "unlisted"
  | "withdraw"
  | "unauthorised"
  | "limit"
  | "unavailable"
  | "disabled"
  | "disabledDeposit"
  | "disabledWithdrawal"
  | "withdrawLimit"
  | "disabledChain"
  | "withdrawUnavailable"
  | "depositUnavailable";

interface UiTooltipUnsupportedProps {
  arrowPlacement?: "top" | "bottom" | "left" | "right";
  children: React.JSX.Element;
  enable?: boolean;
  className?: string;
  reason?: SupRecordUnsupportedReason; // reason effects the tooltip text
}

export default function UiTooltipUnsupported({ children, arrowPlacement, enable, className, reason = "token" }: UiTooltipUnsupportedProps) {
  const { data: adminConfig } = useGetAdminConfig();

  const UNSUPPORTED_DICTIONARY = useMemo(
    () =>
      ({
        vault: {
          title: "UNSUPPORTED VAULT",
          text: "This vault's underlying token is not supported on the Superform App.",
          color: "warning",
        },
        token: {
          title: "UNSUPPORTED TOKEN",
          text: "You can't use this token with the Superform App.",
          color: "warning",
        },
        unlisted: {
          title: "NOT LISTED",
          text: "You can't deposit to this vault as it has no superform",
          color: "warning",
        },
        unavailable: {
          title: "NOT AVAILABLE",
          text: "Transactions are not available for this vault",
          color: "warning",
        },
        depositUnavailable: {
          title: "NOT AVAILABLE",
          text: "Deposits are not available for this vault",
          color: "warning",
        },
        withdrawUnavailable: {
          title: "NOT AVAILABLE",
          text: "Withdrawals are not available for this vault",
          color: "warning",
        },
        withdraw: {
          title: "CAN'T WITHDRAW",
          text: "You can't withdraw SuperPositions minted on different chains at the same time",
          color: "info",
        },
        unauthorised: {
          title: "UNAUTHORIZED",
          text: "You need to connect your wallet first",
          color: "info",
        },
        limit: {
          title: "DEPOSIT BAG LIMIT REACHED",
          text: `You can't add more than ${adminConfig?.depositCartLimit ?? 1} vault${(adminConfig?.depositCartLimit ?? 1) > 1 ? "s" : ""} to the deposit bag (temporary limit)`,
          color: "primary",
        },
        withdrawLimit: {
          title: "WITHDRAWAL BAG LIMIT REACHED",
          text: `You can't add more than ${adminConfig?.withdrawalCartLimit ?? 1} vault${
            (adminConfig?.depositCartLimit ?? 1) > 1 ? "s" : ""
          } to the withdrawal bag (temporary limit)`,
          color: "primary",
        },
        disabledWithdrawal: {
          title: "DISABLED",
          text: process.env.NEXT_PUBLIC_DISABLE_WITHDRAW_MSG || "All withdrawals are temporarily disabled",
          color: "warning",
        },
        disabledDeposit: {
          title: "DISABLED",
          text: process.env.NEXT_PUBLIC_DISABLE_DEPOSIT_MSG || "All deposits are temporarily disabled",
          color: "warning",
        },
        disabled: {
          title: "DISABLED",
          text: process.env.NEXT_PUBLIC_DISABLE_DEPOSIT_WITHDRAW_MSG || "All deposits and withdrawals are temporarily disabled",
          color: "warning",
        },
        disabledChain: {
          title: "CHAIN DISABLED",
          text: process.env.NEXT_PUBLIC_DISABLE_DEPOSIT_WITHDRAW_MSG || "Operations with this chain are temporarily disabled",
          color: "warning",
        },
      }) as Record<SupRecordUnsupportedReason, { title: string; text: string; color: "primary" | "info" | "error" | "warning" | "success" }>,
    [adminConfig]
  );

  const { title, text, color } = UNSUPPORTED_DICTIONARY[reason];
  return (
    <UiTooltip
      wrapper
      as={"div"}
      className={className}
      content={
        <div className="flex flex-col items-start justify-center gap-2 self-stretch">
          <SupText variant="caption" className="font-bold">
            {title}
          </SupText>
          <SupText variant="body2" className="uppercase">
            {text}
          </SupText>
        </div>
      }
      color={color}
      arrowPlacement={arrowPlacement}
      enable={enable}
    >
      {children}
    </UiTooltip>
  );
}
